import { createDefaultProfile, defaultNodeTypeForPosition } from '../defaults'
import { normalizeLoraRows } from './loraPairing'
import { normalizeSegmentPositions } from './segments'
import type { AppProfile, LoraMode, Mode, NodeType, Segment } from '../types'

const VALID_NODE_TYPES = new Set<NodeType>(['i2v', 'flf2v', 'svi', 'sviflf'])

const segmentHasLora = (segment: { lora_high: unknown; lora_low: unknown }): boolean =>
  [...normalizeLoraRows(segment.lora_high), ...normalizeLoraRows(segment.lora_low)]
    .some((lora) => lora.name && lora.name.toLowerCase() !== 'none')

const normalizeSegment = (
  value: unknown,
  index: number,
  total: number,
  fallback: Segment,
): Segment => {
  const source = value && typeof value === 'object' ? value as Record<string, unknown> : {}
  const rawNodeType = typeof source.node_type === 'string' ? source.node_type.toLowerCase() : ''
  const nodeType = VALID_NODE_TYPES.has(rawNodeType as NodeType)
    ? rawNodeType as NodeType
    : defaultNodeTypeForPosition(index, total)
  const frameCount = Number(source.frame_count)
  const provisional = {
    lora_high: source.lora_high,
    lora_low: source.lora_low,
  }
  const explicitMode = source.lora_mode === 'custom' || source.lora_mode === 'global'
    ? source.lora_mode as LoraMode
    : null
  return {
    id: typeof source.id === 'string' && source.id ? source.id : crypto.randomUUID(),
    prompt: typeof source.prompt === 'string' ? source.prompt : fallback.prompt,
    frame_count: Number.isFinite(frameCount) && frameCount > 0 ? frameCount : fallback.frame_count,
    node_type: nodeType,
    lora_high: normalizeLoraRows(source.lora_high),
    lora_low: normalizeLoraRows(source.lora_low),
    lora_mode: explicitMode ?? (segmentHasLora(provisional) ? 'custom' : 'global'),
    ...(typeof source.end_image === 'string' && source.end_image
      ? { end_image: source.end_image }
      : {}),
  }
}

export function normalizeProfile(value: unknown): AppProfile {
  const defaults = createDefaultProfile()
  if (!value || typeof value !== 'object') return defaults
  const source = value as Record<string, unknown>
  const rawGlobal = source.gs && typeof source.gs === 'object'
    ? source.gs as Record<string, unknown>
    : {}
  const {
    notifications_enabled: _legacyNotificationsEnabled,
    notification_volume: _legacyNotificationVolume,
    ...portableGlobal
  } = rawGlobal
  const gs = { ...defaults.gs, ...portableGlobal }
  const savedGlobalLoraRows = Math.max(
    Array.isArray(rawGlobal.global_lora_high) ? rawGlobal.global_lora_high.length : 0,
    Array.isArray(rawGlobal.global_lora_low) ? rawGlobal.global_lora_low.length : 0,
  )
  const globalLoraRowCount = Math.max(2, Math.min(4, savedGlobalLoraRows))
  gs.global_lora_high = normalizeLoraRows(rawGlobal.global_lora_high, globalLoraRowCount)
  gs.global_lora_low = normalizeLoraRows(rawGlobal.global_lora_low, globalLoraRowCount)
  gs.seed = Number.isFinite(Number(gs.seed)) ? Number(gs.seed) : -1

  const rawSegments = Array.isArray(source.segments) && source.segments.length >= 2
    ? source.segments
    : defaults.segments
  const segments = normalizeSegmentPositions(rawSegments.map((segment, index) =>
    normalizeSegment(segment, index, rawSegments.length, defaults.segments[Math.min(index, 2)]),
  ))

  const rawSimple = source.simple_segment
  const simpleSegment = normalizeSegment(rawSimple, 0, 1, defaults.simple_segment)
  if (simpleSegment.node_type !== 'i2v' && simpleSegment.node_type !== 'flf2v') {
    simpleSegment.node_type = 'flf2v'
  }

  const mode: Mode = source.mode === 'simple' ? 'simple' : 'multi'
  return { gs, segments, simple_segment: simpleSegment, mode }
}

const namespace = (email: string) => `mugen2.profiles.${email || 'local'}`

export function readProfiles(email: string): Record<string, AppProfile> {
  try {
    const parsed = JSON.parse(localStorage.getItem(namespace(email)) || '{}') as Record<string, unknown>
    return Object.fromEntries(Object.entries(parsed).map(([name, profile]) => [name, normalizeProfile(profile)]))
  } catch {
    return {}
  }
}

export function writeProfiles(email: string, profiles: Record<string, AppProfile>): void {
  localStorage.setItem(namespace(email), JSON.stringify(profiles))
}

export function serializeProfile(profile: AppProfile): string {
  const clean = normalizeProfile(profile)
  const payload = {
    gs: clean.gs,
    segments: clean.segments.map(({ id: _id, ...segment }) => segment),
    simple_segment: (({ id: _id, ...segment }) => segment)(clean.simple_segment),
    mode: clean.mode,
  }
  return JSON.stringify(payload, null, 2)
}
